import { AlertTriangle } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { getWebStorage } from "@/common/utils/storage";
import { isNative } from "@/common/utils/environment";

/**
 * Warns when the browser gives us no web storage, which makes signing in fail in
 * a way the user would otherwise have no explanation for.
 *
 * With storage blocked, the Supabase session lives only in memory: the user can
 * sign in and use the app, but the session is gone on the next reload, so they
 * appear to be signed out again for no reason. iOS Safari's "Block All Cookies"
 * is the common cause — ~176 affected users in 30 days (Sentry SAILE-WEB-GQ /
 * GP / B9), on the OAuth callback route.
 *
 * There is no silent workaround to fall back to: when site data is blocked,
 * sessionStorage and cookies are unavailable for the same reason, so telling the
 * user is the only real remedy.
 *
 * Native platforms use SecureStorage and are never affected.
 */
export const BlockedStorageWarning = () => {
  if (isNative() || getWebStorage() !== null) {
    return null;
  }

  return (
    <Alert variant="destructive" className="mb-6 text-left">
      <AlertTriangle className="h-5 w-5" />
      <AlertTitle>Your browser is blocking site data</AlertTitle>
      <AlertDescription>
        You can sign in, but you&apos;ll be signed out again as soon as the page
        reloads. To stay signed in, allow cookies and site data for this site in
        your browser settings, then reload this page.
      </AlertDescription>
    </Alert>
  );
};
